import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  NavigationCustomizationItemDto,
  NavigationDtoResponse,
  PageDto,
} from '@asset-metrix/portal-gateway-client-js';
import {
  HorizontalNavigation,
  type NavigationItemData,
  TextWithTooltip,
} from '@asset-metrix/portal-ui';
import { AppShell } from '@mantine/core';
import { useDebouncedState } from '@mantine/hooks';
import clsx from 'clsx';

import { NO_ROLE_ID } from '../../api/portalGatewayClient';
import { useStore } from '../../store';
import { pageWithRoleLink } from '../../utils/urlPath';

import { getActiveItem, getActiveItemIndex } from './activeItems';

import styles from './topNavigation.module.scss';

type LinkContentProps = {
  activeSlug: string;
  item: NavigationItemData;
  onClick: () => void;
  pageType: PageDto['type'];
  urlSearchString: string;
};

function LinkContent({
  activeSlug,
  item,
  onClick,
  pageType,
  urlSearchString,
}: LinkContentProps) {
  const roleId = useStore((state) => state.activeUserRole?.id);
  const isActive = activeSlug === item.completeSlug;
  const isMainLink = item.completeSlug.split('/').length === 2;
  return (
    <Link
      className={clsx(
        styles['subItem'],
        isActive && styles['subItem--active'],
        isMainLink && styles['subItem--main']
      )}
      to={pageWithRoleLink(roleId ?? NO_ROLE_ID, {
        pageType,
        slug: `${item.completeSlug}${urlSearchString.length > 0 ? '?' + urlSearchString : ''}`,
      })}
      onClick={onClick}
    >
      <TextWithTooltip
        textProps={{ lineClamp: isMainLink ? 1 : 2 }}
        style={isMainLink ? { wordBreak: 'break-all' } : undefined}
      >
        {item.label}
      </TextWithTooltip>
    </Link>
  );
}

type TopNavigationProps = {
  navigationData?: NavigationDtoResponse;
  pageType: PageDto['type'];
  slug?: string;
  activeDynamicNavigationItem?: NavigationCustomizationItemDto;
};

export const TopNavigation = ({
  navigationData,
  pageType,
  slug,
  activeDynamicNavigationItem,
}: TopNavigationProps) => {
  const [isOpen, setOpen] = useDebouncedState(false, 200);
  useEffect(() => {
    document.body.classList.toggle('with-subnavigation--open', isOpen);
  }, [isOpen]);
  if (!navigationData || slug === undefined) {
    return null;
  }

  const activeMainItem = getActiveItem(
    navigationData.data[
      pageType === 'page'
        ? 'rootNavigationItems'
        : 'detailPageRootNavigationItems'
    ],
    slug
  );
  if (activeMainItem?.type !== 'navigation-menu-item') {
    return null;
  }

  const activeColumnIndex = getActiveItemIndex(activeMainItem.items, slug);

  const urlParts = activeDynamicNavigationItem?.href.split('?');
  const urlSearchString =
    urlParts != null && urlParts?.length > 1 ? (urlParts.pop() ?? '') : '';

  return (
    <AppShell.Header
      onMouseEnter={() => {
        if (
          activeMainItem?.items?.some(
            (item) =>
              item.type === 'navigation-menu-item' &&
              item.items &&
              item.items.length > 0
          )
        ) {
          setOpen(true);
        }
      }}
      onMouseLeave={() => setOpen(false)}
      className={styles['root']}
    >
      <HorizontalNavigation
        isOpen={isOpen}
        activeColumnIndex={activeColumnIndex}
        navigation={activeMainItem.items}
        Link={(item) => (
          <LinkContent
            activeSlug={slug}
            item={item}
            onClick={() => setOpen(false)}
            pageType={pageType}
            urlSearchString={urlSearchString}
          />
        )}
      />
      <div className={styles['backdrop']}></div>
    </AppShell.Header>
  );
};
